import { useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  Building2,
  CheckCircle2,
  CircleDot,
  Clock,
  History,
  Info,
  MessageSquarePlus,
  MessageSquareReply,
  School,
  Send,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { useSlaAprovacaoSme } from '@/modulos/admin/parametros/slaAprovacaoSme/dados';
import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { cn } from '@/nucleo/utilitarios';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { DetailHeader } from '@/ui/DetailHeader';
import { DetailWithTimeline } from '@/ui/DetailWithTimeline';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { ProgressTimeline, type PassoDeProgresso } from '@/ui/ProgressTimeline';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/base/dialog';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Skeleton } from '@/ui/base/skeleton';
import { Textarea } from '@/ui/base/textarea';

import { useDres } from '../hooks/useDres';
import {
  ROTULO_TIPO,
  STATUS_ENCERRADOS,
  formatarData,
  useBaseDasSolicitacoes,
  usePermissoesDeSolicitacao,
} from './apresentacao';
import { SeloDeSituacao } from './SeloDeSituacao';
import { useSolicitacaoSme } from './dados';
import type { OrigemSolicitacaoSme, SolicitacaoSmeDetalhe } from './tipos';

/** Quem abre e quem analisa, conforme o sentido da solicitação. */
function lados(origem: OrigemSolicitacaoSme) {
  return origem === 'SME' ? { abriu: 'SME', analisa: 'DTP' } : { abriu: 'DTP', analisa: 'SME' };
}

function ehFimDeSemana(data: Date): boolean {
  const dia = data.getDay();
  return dia === 0 || dia === 6;
}

function adicionarDiasUteis(data: Date, dias: number): Date {
  const resultado = new Date(data);
  let restantes = dias;
  while (restantes > 0) {
    resultado.setDate(resultado.getDate() + 1);
    if (!ehFimDeSemana(resultado)) restantes -= 1;
  }
  return resultado;
}

/** Dias úteis de `de` até `ate`, negativo se `ate` já passou. */
function diferencaDiasUteis(de: Date, ate: Date): number {
  const sinal = ate.getTime() >= de.getTime() ? 1 : -1;
  const inicio = new Date(sinal > 0 ? de : ate);
  const fim = new Date(sinal > 0 ? ate : de);
  inicio.setHours(0, 0, 0, 0);
  fim.setHours(0, 0, 0, 0);
  let contagem = 0;
  const cursor = new Date(inicio);
  while (cursor < fim) {
    cursor.setDate(cursor.getDate() + 1);
    if (!ehFimDeSemana(cursor)) contagem += 1;
  }
  return sinal * contagem;
}

/**
 * Cronômetro do prazo de resposta — `SlaDemandaCard` do protótipo: o prazo
 * padrão é o SLA de aprovação SME configurado; se quem abriu pediu data
 * anterior, ela prevalece.
 */
function CartaoSla({ solicitacao }: { solicitacao: SolicitacaoSmeDetalhe }) {
  const { data: sla } = useSlaAprovacaoSme();
  const diasUteis = sla?.diasUteis ?? 2;
  const { abriu, analisa } = lados(solicitacao.origem);

  const prazo = useMemo(() => {
    const padrao = adicionarDiasUteis(new Date(solicitacao.criadaEm), diasUteis);
    const desejado = solicitacao.prazoDesejado
      ? new Date(`${solicitacao.prazoDesejado}T00:00:00`)
      : null;
    const vale = desejado && desejado.getTime() < padrao.getTime() ? desejado : padrao;
    return { em: vale, doDesejado: vale === desejado };
  }, [solicitacao.criadaEm, solicitacao.prazoDesejado, diasUteis]);

  if (STATUS_ENCERRADOS.includes(solicitacao.status)) {
    const fechadaEm = solicitacao.atendidaEm ? new Date(solicitacao.atendidaEm) : null;
    const noPrazo = fechadaEm ? fechadaEm.getTime() <= prazo.em.getTime() : null;
    return (
      <Card className="border-border/60">
        <CardContent className="flex items-center gap-3 p-4">
          <CheckCircle2
            className={cn('h-8 w-8 shrink-0', noPrazo === false ? 'text-warning' : 'text-success')}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">
              {solicitacao.status === 'ATENDIDA' && `Atendida pela ${analisa}`}
              {solicitacao.status === 'RECUSADA' && `Recusada pela ${analisa}`}
              {solicitacao.status === 'CANCELADA' && `Cancelada pela ${abriu}`}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {fechadaEm
                ? `Em ${formatarData(fechadaEm.toISOString(), true)}`
                : 'Sem registro de data'}
              {noPrazo !== null && (
                <span className="ml-2">{noPrazo ? '· dentro do prazo' : '· fora do prazo'}</span>
              )}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const restantes = diferencaDiasUteis(new Date(), prazo.em);
  const vencido = restantes < 0;
  const vencendo = !vencido && restantes <= 1;
  const Icone = vencido ? AlertTriangle : Clock;

  return (
    <Card
      className={cn(
        vencido
          ? 'border-destructive/40 bg-destructive/5'
          : vencendo
            ? 'border-warning/40 bg-warning/5'
            : 'border-border/60',
      )}
    >
      <CardContent className="flex items-start gap-3 p-4">
        <Icone
          className={cn(
            'mt-0.5 h-8 w-8 shrink-0',
            vencido ? 'text-destructive' : vencendo ? 'text-warning' : 'text-muted-foreground',
          )}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold">
              {vencido
                ? `SLA vencido há ${Math.abs(restantes)} dia${Math.abs(restantes) === 1 ? ' útil' : 's úteis'}`
                : vencendo
                  ? restantes === 0
                    ? 'Vence hoje'
                    : 'Vence em 1 dia útil'
                  : `${restantes} dias úteis restantes`}
            </p>
            {vencido && <Badge variant="destructive">Atrasada</Badge>}
            {vencendo && (
              <Badge variant="outline" className="border-warning text-warning">
                Vencendo
              </Badge>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Prazo {analisa}: <strong>{formatarData(prazo.em.toISOString())}</strong>{' '}
            <span>
              (
              {prazo.doDesejado
                ? `prazo desejado pela ${abriu}`
                : `padrão de ${diasUteis} dias úteis`}
              )
            </span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/** Etapas do "Progresso da demanda" — `buildDemandaSteps` do protótipo. */
function passosDaSolicitacao(
  s: SolicitacaoSmeDetalhe,
  aguardandoComplemento: boolean,
): PassoDeProgresso[] {
  const { abriu, analisa } = lados(s.origem);
  const aberta = s.status === 'ABERTA';
  const emAnalise = s.status === 'EM_ANALISE';
  const encaminhada = s.status === 'ENCAMINHADA_DRE';
  const respondida = s.status === 'RESPONDIDA_DRE';
  const encerrada = STATUS_ENCERRADOS.includes(s.status);
  const passouDaAnalise = encaminhada || respondida || encerrada;
  const passouDaDre = respondida || s.status === 'ATENDIDA' || s.status === 'RECUSADA';
  const prazoVencido =
    s.prazoDesejado !== null &&
    new Date(`${s.prazoDesejado}T23:59:59`).getTime() < Date.now() &&
    !encerrada;

  const passos: PassoDeProgresso[] = [
    {
      label: `Demanda enviada pela ${abriu}`,
      status: 'completed',
      date: s.criadaEm,
      origem: abriu,
      description: s.prazoDesejado ? `Prazo desejado: ${formatarData(s.prazoDesejado)}` : undefined,
      pendencia: prazoVencido
        ? { label: 'Prazo desejado vencido', severidade: 'media' }
        : undefined,
    },
    {
      label: aguardandoComplemento
        ? `Aguardando complemento da ${abriu}`
        : `Em análise pela ${analisa}`,
      status: aberta
        ? 'current'
        : emAnalise
          ? aguardandoComplemento
            ? 'warning'
            : 'current'
          : passouDaAnalise
            ? 'completed'
            : 'pending',
      origem: aguardandoComplemento ? `${analisa} → ${abriu}` : analisa,
      description: aguardandoComplemento
        ? `${analisa} solicitou informações adicionais à ${abriu}`
        : aberta
          ? `Aguardando triagem inicial da ${analisa}`
          : undefined,
      proximaAcao: aguardandoComplemento
        ? `${abriu} deve responder ao pedido para a análise prosseguir`
        : aberta
          ? `${analisa} deve assumir a análise`
          : undefined,
    },
  ];

  if (encaminhada || respondida || s.encaminhadaDreEm || s.respostaDreEm) {
    passos.push({
      label: encaminhada ? 'Encaminhada à DRE' : 'Respondida pela DRE',
      status: encaminhada ? 'current' : passouDaDre ? 'completed' : 'pending',
      date: encaminhada ? s.encaminhadaDreEm : (s.respostaDreEm ?? s.atualizadaEm),
      origem: encaminhada ? `${analisa} → DRE` : 'DRE',
      description:
        respondida && s.respostaDre
          ? s.respostaDre.slice(0, 120)
          : encaminhada
            ? 'Aguardando manifestação da DRE'
            : undefined,
      proximaAcao: encaminhada
        ? `DRE precisa responder ao encaminhamento`
        : respondida
          ? `${analisa} deve concluir a tratativa após a resposta da DRE`
          : undefined,
    });
  }

  if (s.status === 'ATENDIDA') {
    passos.push({
      label: `Atendida pela ${analisa}`,
      status: 'completed',
      date: s.atendidaEm ?? s.atualizadaEm,
      origem: analisa,
      description: s.respostaFinal?.slice(0, 120),
    });
  } else if (s.status === 'RECUSADA') {
    passos.push({
      label: `Recusada pela ${analisa}`,
      status: 'error',
      date: s.atualizadaEm,
      origem: analisa,
      description: s.respostaFinal?.slice(0, 200),
    });
  } else if (s.status === 'CANCELADA') {
    passos.push({
      label: `Cancelada pela ${abriu}`,
      status: 'error',
      date: s.atualizadaEm,
      origem: abriu,
    });
  } else {
    passos.push({
      label: `Resposta da ${analisa}`,
      status: 'pending',
      description: 'Aguardando decisão final',
    });
  }
  return passos;
}

const EVENTOS: Record<
  string,
  { rotulo: (s: SolicitacaoSmeDetalhe) => string; icone: LucideIcon; tom: string }
> = {
  CRIAR: { rotulo: () => 'Demanda criada', icone: Send, tom: 'text-primary' },
  ANALISAR: {
    rotulo: (s) => `Em análise pela ${lados(s.origem).analisa}`,
    icone: CircleDot,
    tom: 'text-info',
  },
  PEDIR_COMPLEMENTO: {
    rotulo: (s) => `${lados(s.origem).analisa} pediu complemento`,
    icone: MessageSquareReply,
    tom: 'text-warning',
  },
  COMPLEMENTAR: { rotulo: () => 'Complemento enviado', icone: MessageSquarePlus, tom: 'text-info' },
  ENCAMINHAR_DRE: { rotulo: () => 'Encaminhada à DRE', icone: Building2, tom: 'text-info' },
  RESPONDER_SME: {
    rotulo: () => 'DRE respondeu à SME',
    icone: MessageSquareReply,
    tom: 'text-info',
  },
  ATENDER: {
    rotulo: (s) => `Atendida pela ${lados(s.origem).analisa}`,
    icone: CheckCircle2,
    tom: 'text-success',
  },
  RECUSAR: {
    rotulo: (s) => `Recusada pela ${lados(s.origem).analisa}`,
    icone: XCircle,
    tom: 'text-destructive',
  },
  CANCELAR: { rotulo: () => 'Cancelada', icone: Ban, tom: 'text-muted-foreground' },
};

function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">{rotulo}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

/** Aviso fixo dos diálogos: aviso passageiro some, e a captura parece envio. */
function AvisoDeNadaRegistrado() {
  return (
    <Alert>
      <Info className="h-4 w-4" aria-hidden="true" />
      <AlertDescription>
        Esta resposta ainda não pode ser registrada. Você pode redigir o texto, mas nada é enviado.
      </AlertDescription>
    </Alert>
  );
}

interface DialogoDeTextoProps {
  aberto: boolean;
  aoMudar: (aberto: boolean) => void;
  titulo: string;
  descricao: string;
  rotulo: string;
  placeholder: string;
  limite: number;
  rotuloDoEnvio: string;
  antes?: ReactNode;
}

/** Os três diálogos de resposta do detalhe têm a mesma forma: um texto e o envio. */
function DialogoDeTexto({
  aberto,
  aoMudar,
  titulo,
  descricao,
  rotulo,
  placeholder,
  limite,
  rotuloDoEnvio,
  antes,
}: DialogoDeTextoProps) {
  const [texto, definirTexto] = useState('');
  const idDoCampo = `dlg-${titulo.replace(/\W+/g, '-').toLowerCase()}`;

  const enviar = () => {
    if (texto.trim().length < 10) {
      toast.error('Texto muito curto', { description: 'Detalhe com pelo menos 10 caracteres.' });
      return;
    }
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  };

  return (
    <Dialog
      open={aberto}
      onOpenChange={(v) => {
        if (!v) definirTexto('');
        aoMudar(v);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>{descricao}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {antes}
          <div className="space-y-1.5">
            <Label htmlFor={idDoCampo}>
              {rotulo} * <span className="text-muted-foreground">(mín. 10 caracteres)</span>
            </Label>
            <Textarea
              id={idDoCampo}
              rows={5}
              value={texto}
              onChange={(e) => definirTexto(e.target.value)}
              placeholder={placeholder}
              maxLength={limite}
            />
            <p className="text-right text-xs text-muted-foreground">
              {texto.length}/{limite}
            </p>
          </div>
          <AvisoDeNadaRegistrado />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => aoMudar(false)}>
            Cancelar
          </Button>
          <Button onClick={enviar}>{rotuloDoEnvio}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Triângulo SME ↔ DRE — `DemandaTrianguloActions` do protótipo. Só existe em
 * questionamento sobre OS aberto pela DTP: a SME repassa à DRE, a DRE
 * responde, a SME consolida.
 *
 * O DETALHE FICA ABERTO À DRE, DE PROPÓSITO (decisão registrada na #136): no
 * protótipo a lista usa `ProtectedSmeDtpRoute`, que barra a DRE, mas o
 * detalhe usa `ProtectedSmeRoute`, que a aceita — é por ali que ela responde
 * à solicitação que a SME encaminhou. `podeResponder` é como a DRE alcança
 * essa ação.
 */
function AcoesDoTriangulo({ solicitacao }: { solicitacao: SolicitacaoSmeDetalhe }) {
  const permissoes = usePermissoesDeSolicitacao();
  const { data: dres } = useDres();
  const [encaminhando, definirEncaminhando] = useState(false);
  const [respondendo, definirRespondendo] = useState(false);
  const [dreId, definirDreId] = useState(solicitacao.dreId);

  if (solicitacao.tipo !== 'QUESTIONAMENTO_OS' || solicitacao.origem !== 'DTP') return null;
  const podeEncaminhar =
    permissoes.atuaComoSme &&
    (solicitacao.status === 'ABERTA' || solicitacao.status === 'EM_ANALISE');
  const podeResponder = permissoes.atuaComoDre && solicitacao.status === 'ENCAMINHADA_DRE';
  if (!podeEncaminhar && !podeResponder) return null;

  return (
    <>
      {podeEncaminhar && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold">Encaminhar à DRE responsável</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Repasse o questionamento à DRE para tratativa. A SME consolida a resposta e
                  devolve à DTP ao final.
                </p>
              </div>
            </div>
            <Button onClick={() => definirEncaminhando(true)} className="w-full sm:w-auto">
              <Send className="mr-1 h-4 w-4" aria-hidden="true" />
              Encaminhar à DRE
            </Button>
          </CardContent>
        </Card>
      )}
      {podeResponder && (
        <Card className="border-warning/40 bg-warning/5">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <MessageSquareReply
                className="mt-0.5 h-5 w-5 shrink-0 text-warning"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm font-semibold">A SME repassou um questionamento à sua DRE</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Responda à SME. A SME consolidará e responderá à DTP.
                </p>
              </div>
            </div>
            <Button onClick={() => definirRespondendo(true)} className="w-full sm:w-auto">
              <MessageSquarePlus className="mr-1 h-4 w-4" aria-hidden="true" />
              Responder à SME
            </Button>
          </CardContent>
        </Card>
      )}

      <DialogoDeTexto
        aberto={encaminhando}
        aoMudar={definirEncaminhando}
        titulo="Encaminhar questionamento à DRE"
        descricao="Selecione a DRE responsável e descreva o que ela precisa esclarecer."
        rotulo="Pedido à DRE"
        placeholder="Ex.: verifique os dias letivos do período e confirme se houve suspensão na escola…"
        limite={2000}
        rotuloDoEnvio="Encaminhar"
        antes={
          <div className="space-y-1.5">
            <Label htmlFor="dlg-dre">DRE responsável *</Label>
            <Select value={dreId} onValueChange={definirDreId}>
              <SelectTrigger id="dlg-dre">
                <SelectValue placeholder="Selecione…" />
              </SelectTrigger>
              <SelectContent>
                {(dres ?? []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.codigo} — {d.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />
      <DialogoDeTexto
        aberto={respondendo}
        aoMudar={definirRespondendo}
        titulo="Responder à SME"
        descricao="Sua resposta será encaminhada à SME, que finalizará a tratativa com a DTP."
        rotulo="Resposta"
        placeholder="Descreva os esclarecimentos solicitados pela SME…"
        limite={4000}
        rotuloDoEnvio="Enviar resposta"
      />
    </>
  );
}

/**
 * Detalhe de uma solicitação DTP ↔ SME — `SmeDemandaDetail` do protótipo.
 *
 * Nenhuma ação grava: cancelar, responder complemento, encaminhar à DRE e
 * responder à SME mostram o aviso de indisponibilidade. Os textos seguem o
 * sentido da solicitação (quem abriu, quem analisa) em vez de fixar
 * SME → DTP, como o protótipo faz até para a solicitação aberta pela DTP.
 *
 * A DRE ABRE O DETALHE, DE PROPÓSITO (decisão registrada na #136), ao
 * contrário da lista (`SolicitacoesSmeListPage.tsx`): no protótipo o
 * detalhe usa `ProtectedSmeRoute`, que aceita a DRE, porque é ali que ela
 * responde a uma solicitação que a SME encaminhou (ver `AcoesDoTriangulo`).
 */
export function SolicitacaoSmeDetalhePage() {
  return <Conteudo />;
}

function Conteudo() {
  const { id = '' } = useParams<{ id: string }>();
  const base = useBaseDasSolicitacoes();
  const permissoes = usePermissoesDeSolicitacao();
  const { data: solicitacao, isLoading, isError, refetch } = useSolicitacaoSme(id);
  const [complementando, definirComplementando] = useState(false);

  const voltarRotulo = base === '/sme/demandas' ? 'Demandas' : 'Solicitações DTP→SME';

  if (isError) {
    return (
      <ErrorState
        variant="load-failed"
        primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
      />
    );
  }
  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!solicitacao) {
    return <ErrorState variant="not-found" title="Solicitação não encontrada" />;
  }

  const { abriu, analisa } = lados(solicitacao.origem);
  const ultimo = solicitacao.eventos[solicitacao.eventos.length - 1];
  const aguardandoComplemento =
    solicitacao.status === 'EM_ANALISE' && ultimo?.acao === 'PEDIR_COMPLEMENTO';
  const doLadoQueAbriu = permissoes.ehDoLadoQueAbriu(solicitacao.origem);
  const podeCancelar = doLadoQueAbriu && solicitacao.status === 'ABERTA';
  const podeComplementar = doLadoQueAbriu && aguardandoComplemento;

  return (
    <div className="space-y-4">
      <DetailHeader
        backTo={base}
        backLabel={voltarRotulo}
        title={`${solicitacao.origem === 'DTP' ? 'Solicitação DTP→SME' : 'Demanda'} #${solicitacao.numero}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span>{solicitacao.titulo}</span>
            <SeloDeSituacao status={solicitacao.status} />
          </span>
        }
        actions={
          podeCancelar ? (
            <ConfirmDialog
              trigger={
                <Button variant="outline">
                  <Ban className="mr-1 h-4 w-4" aria-hidden="true" />
                  Cancelar demanda
                </Button>
              }
              title="Cancelar esta demanda?"
              description="Cancelar demandas ainda não está disponível. Nada é cancelado nem a outra parte é notificada até o endpoint de solicitações existir."
              confirmLabel="Cancelar demanda"
              variant="destructive"
              onConfirm={() => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)}
            />
          ) : undefined
        }
      />

      {solicitacao.tipo === 'QUESTIONAMENTO_OS' && (
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="gap-1.5 border-info/40 bg-info/10 py-1 text-foreground"
          >
            <CircleDot className="h-3.5 w-3.5 text-info" aria-hidden="true" />
            Triângulo SME ↔ DRE
          </Badge>
          <span className="text-xs text-muted-foreground">
            Questionamento da DTP — a SME pode encaminhar à DRE para subsidiar a resposta.
          </span>
        </div>
      )}

      {permissoes.atuaComoDtp && solicitacao.origem === 'SME' && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:gap-6 sm:p-4">
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="text-muted-foreground">DRE:</span>
              <span className="font-semibold">{solicitacao.dreNome}</span>
            </div>
            {solicitacao.escolaNome && (
              <div className="flex items-center gap-2 text-sm">
                <School className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span className="text-muted-foreground">Escola:</span>
                <span className="font-semibold">{solicitacao.escolaNome}</span>
              </div>
            )}
            <Badge variant="outline" className="self-start sm:ml-auto sm:self-auto">
              Solicitação enviada pela SME
            </Badge>
          </CardContent>
        </Card>
      )}

      <CartaoSla solicitacao={solicitacao} />

      {podeComplementar && (
        <Card className="border-warning/40 bg-warning/5">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <MessageSquareReply
                className="mt-0.5 h-5 w-5 shrink-0 text-warning"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm font-semibold">{analisa} solicitou informações adicionais</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Responda ao pedido para que a análise prossiga.
                </p>
              </div>
            </div>
            <Button onClick={() => definirComplementando(true)} className="w-full sm:w-auto">
              <MessageSquarePlus className="mr-1 h-4 w-4" aria-hidden="true" />
              Responder pedido
            </Button>
          </CardContent>
        </Card>
      )}

      <AcoesDoTriangulo solicitacao={solicitacao} />

      <DetailWithTimeline
        timeline={
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Progresso da demanda</CardTitle>
            </CardHeader>
            <CardContent>
              <ProgressTimeline passos={passosDaSolicitacao(solicitacao, aguardandoComplemento)} />
            </CardContent>
          </Card>
        }
      >
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Detalhes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Campo rotulo="Tipo">{ROTULO_TIPO[solicitacao.tipo]}</Campo>
              <Campo rotulo="DRE">{solicitacao.dreNome}</Campo>
              {solicitacao.escolaNome && <Campo rotulo="Escola">{solicitacao.escolaNome}</Campo>}
              <Campo rotulo="Justificativa">
                <p className="whitespace-pre-wrap">{solicitacao.justificativa}</p>
              </Campo>
              {solicitacao.prazoDesejado && (
                <Campo rotulo="Prazo desejado">{formatarData(solicitacao.prazoDesejado)}</Campo>
              )}
              {solicitacao.encaminhamentoDre && (
                <Campo
                  rotulo={`Encaminhamento à DRE${solicitacao.encaminhadaDreEm ? ` · ${formatarData(solicitacao.encaminhadaDreEm, true)}` : ''}`}
                >
                  <p className="whitespace-pre-wrap rounded-lg border border-primary/20 bg-primary/5 p-3">
                    {solicitacao.encaminhamentoDre}
                  </p>
                </Campo>
              )}
              {solicitacao.respostaDre && (
                <Campo
                  rotulo={`Resposta da DRE${solicitacao.respostaDreEm ? ` · ${formatarData(solicitacao.respostaDreEm, true)}` : ''}`}
                >
                  <p className="whitespace-pre-wrap rounded-lg bg-muted p-3">
                    {solicitacao.respostaDre}
                  </p>
                </Campo>
              )}
              {solicitacao.respostaFinal && (
                <Campo rotulo={`Resposta final da ${analisa} à ${abriu}`}>
                  <p className="whitespace-pre-wrap rounded-lg bg-muted p-3">
                    {solicitacao.respostaFinal}
                  </p>
                </Campo>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-4 w-4" aria-hidden="true" />
                Histórico de eventos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {solicitacao.eventos.length === 0 ? (
                <EmptyState
                  icon={History}
                  title="Sem histórico"
                  description={`Eventos aparecem aqui conforme a ${analisa} atua.`}
                />
              ) : (
                <ol className="space-y-3 text-sm">
                  {solicitacao.eventos.map((evento) => {
                    const conhecido = EVENTOS[evento.acao];
                    const Icone = conhecido?.icone ?? CircleDot;
                    return (
                      <li key={evento.id} className="flex gap-3">
                        <div
                          className={cn(
                            'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted',
                            conhecido?.tom ?? 'text-muted-foreground',
                          )}
                        >
                          <Icone className="h-3.5 w-3.5" aria-hidden="true" />
                        </div>
                        <div className="min-w-0 flex-1 border-b border-border/50 pb-2 last:border-0">
                          <p className="font-medium leading-tight">
                            {conhecido?.rotulo(solicitacao) ?? evento.acao}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatarData(evento.criadoEm, true)}
                          </p>
                          {evento.detalhe && (
                            <p className="mt-1 whitespace-pre-wrap rounded bg-muted/40 p-2 text-xs">
                              {evento.detalhe.slice(0, 400)}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
              <div className="mt-4 space-y-1 border-t pt-3 text-xs text-muted-foreground">
                <p>
                  <strong>Criada:</strong> {formatarData(solicitacao.criadaEm, true)}
                </p>
                {solicitacao.atendidaEm && (
                  <p>
                    <strong>Atendida:</strong> {formatarData(solicitacao.atendidaEm, true)}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DetailWithTimeline>

      {podeComplementar && (
        <DialogoDeTexto
          aberto={complementando}
          aoMudar={definirComplementando}
          titulo="Responder pedido de complemento"
          descricao={`Sua resposta será enviada à ${analisa} para que a análise prossiga.`}
          rotulo="Sua resposta"
          placeholder={`Descreva o complemento solicitado pela ${analisa}…`}
          limite={1500}
          rotuloDoEnvio="Enviar resposta"
        />
      )}
    </div>
  );
}

export default SolicitacaoSmeDetalhePage;
